import React, { useContext } from "react"
import styled, { ThemeContext } from "styled-components"
import { useTranslation } from "react-i18next"
import {
  containerSmallMaxWidth,
  MEDIA_MIN_MEDIUM,
  MEDIA_MIN_LARGE
} from "../../constants"
import { usePage } from "../context/PageContext"
import { BrinkContext } from "../context/BrinkContext"
import Link from "../Link"
import BottomBar from "./BottomBar"
import NewsletterSignup from "./NewsletterSignup"
import Logo from "../../images/rondure-logo.svg"
import ShippingAndPayementLogos from "./ShippingAndPayementLogos"

const Wrapper = styled.div`
  flex-shrink: 0;
`

const PaddingWrapper = styled.div`
  padding: 2rem 2.5rem 3rem;

  ${MEDIA_MIN_LARGE} {
    padding: 0 0 3rem;
  }
`

const Container = styled.div`
  flex-shrink: 0;
  padding: 4rem 0 3rem;
  max-width: ${containerSmallMaxWidth};
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  color: ${(p) => p.theme.colors.black};

  ${MEDIA_MIN_LARGE} {
    justify-content: space-between;
    flex-wrap: nowrap;
    padding: 10rem 0 5rem;
    border-bottom: 0.1rem solid ${(p) => p.theme.colors.lightBorder};
  }
`

const Heading = styled.h3`
  width: 100%;
  margin-bottom: 2rem;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.1rem;
  color: ${(p) => p.theme.colors.black};

  ${MEDIA_MIN_LARGE} {
    margin-bottom: 4.5rem;
  }
`

const Links = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3.5rem;
  order: 1;

  ${MEDIA_MIN_LARGE} {
    order: 2;
  }

  > div {
    width: 100%;

    ${MEDIA_MIN_MEDIUM} {
      flex: 1;
      width: auto;
    }
  }

  ${MEDIA_MIN_LARGE} {
    border-bottom: none;
    margin-bottom: 0;
  }
`

const LinkList = styled.ul`
  list-style: none;
  padding: 0 0 2.5rem;
  margin: 0 0 2rem;
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.lightBorder};

  ${MEDIA_MIN_MEDIUM} {
    border-bottom: none;
  }

  ${MEDIA_MIN_LARGE} {
    margin: -3rem 0 0;
  }
`

const SocialLinks = styled(LinkList)`
  display: flex;
`

const ListItem = styled.li`
  display: flex;
  align-items: center;

  a {
    font-size: 1.6rem;
    line-height: 1.8rem;
    margin: 1.4rem 0;
    letter-spacing: 0.05rem;
    display: inline-block;
    color: ${(p) => p.theme.colors.black};

    &:after {
      bottom: -0.6rem;
    }
  }

  i {
    font-size: 1.8rem;
    margin-right: 1rem;
    margin-top: 0.2rem;
    padding-bottom: 0.5rem;
  }
`

const Left = styled.div`
  width: 100%;
  padding: 2rem 0 0;
  order: 2;
  text-align: center;

  ${MEDIA_MIN_LARGE} {
    order: 1;
    min-width: 35rem;
    flex: 1;
    text-align: left;
    padding: 2rem 10rem 3rem 0;
    margin-right: 10rem;
    border-right: 0.1rem solid ${(p) => p.theme.colors.lightBorder};
  }

  p {
    font-size: 1.8rem;
    margin: 2rem 0 3rem;
  }
`

const HomeLink = styled(Link)`
  display: block;
  margin: 0 auto;
  width: 100%;

  svg {
    max-width: 12rem;
    padding: 0;
    fill: ${(p) => p.theme.colors.black};
  }
`

const SocialLink = styled.a`
  padding-right: 1rem;
`

const Footer = React.memo(() => {
  const { colors } = useContext(ThemeContext)
  const { languageCode } = useContext(BrinkContext)
  const { footerMenu, buildLanguage } = usePage()
  const { t } = useTranslation("translations")

  return (
    <Wrapper>
      <NewsletterSignup />
      <PaddingWrapper>
        <Container>
          <Links>
            {footerMenu &&
              footerMenu
                .sort((a, b) => {
                  return a.sortOrder - b.sortOrder
                })
                .map((menu) => (
                  <div key={menu._id}>
                    <Heading>
                      {menu.title &&
                        (menu.title[languageCode] || menu.title.en)}
                    </Heading>
                    <LinkList>
                      {menu.menuItems &&
                        menu.menuItems.map((item) => (
                          <ListItem key={item._id}>
                            <Link
                              inverted
                              underline
                              color={colors.black}
                              to={`/${
                                item.slug[buildLanguage] || item.slug.en
                              }`}
                            >
                              {item.title[languageCode] || item.title.en}
                            </Link>
                          </ListItem>
                        ))}
                    </LinkList>
                  </div>
                ))}
            <div>
              <Heading>{t("Follow us on")}</Heading>
              <SocialLinks>
                <ListItem>
                  <SocialLink
                    href="https://www.facebook.com/rondure3141"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-facebook fa-fw"></i>
                  </SocialLink>
                </ListItem>
                <ListItem>
                  <SocialLink
                    href="https://www.instagram.com/rondure3141/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-instagram fa-fw"></i>
                  </SocialLink>
                </ListItem>
                {/* 
                <ListItem>
                  <SocialLink
                    href="https://www.youtube.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-youtube fa-fw"></i>
                  </SocialLink>
                </ListItem>
                */}
              </SocialLinks>
            </div>
          </Links>
          <Left>
            <HomeLink to="/" title="Go to start page">
              <Logo alt="Rondure logo" />
            </HomeLink>
          </Left>
        </Container>
        <ShippingAndPayementLogos />
      </PaddingWrapper>
      <BottomBar />
    </Wrapper>
  )
})

export default Footer
